import { RowModel, flexRender } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { isInteractiveEventTarget } from '@/shared/lib/utils/isInteractiveEventTarget';
import { NavigationConfig } from '../../ui/AppTable/AppTable';
import styles from './TableBody.module.scss';

interface TableBodyProps<TData> {
  rows: RowModel<TData>['rows'];
  navigationConfig?: NavigationConfig<TData>[];
  onRowClick?: (row: TData) => void;
  getRowClassName?: (row: TData) => string | undefined;
  columnMinWidths?: Record<string, number>;
}

export const TableBody = <T,>({
  rows,
  navigationConfig,
  onRowClick,
  getRowClassName,
  columnMinWidths,
}: TableBodyProps<T>) => {
  const getNavigationConfig = (columnId: string) =>
    navigationConfig?.find((config) => config.columnId === columnId);

  return (
    <tbody>
      {rows.map((row) => (
        <tr
          key={row.id}
          className={`${styles.row} ${onRowClick ? styles.clickable : ''} ${getRowClassName?.(row.original) ?? ''}`}
          onClick={(event) => {
            if (!onRowClick) return;
            if (isInteractiveEventTarget(event.target)) return;
            onRowClick(row.original);
          }}
        >
          {row.getVisibleCells().map((cell) => {
            const navConfig = getNavigationConfig(cell.column.id);
            const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext());

            return (
              <td
                key={cell.id}
                className={styles.cell}
                style={{
                  width: `${cell.column.getSize()}px`,
                  minWidth: cell.column.columnDef.minSize ?? columnMinWidths?.[cell.column.id],
                }}
                onClick={(event) => {
                  if (isInteractiveEventTarget(event.target)) {
                    event.stopPropagation();
                  }
                }}
              >
                {navConfig ? (
                  <Link to={navConfig.getUrl(row.original)} className={styles.navigationLink}>
                    {cellContent}
                  </Link>
                ) : (
                  cellContent
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  );
};
